import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'react-hot-toast';
import ScrollToTop from './components/ScrollToTop';

// Layouts
import AdminLayout from './admin/layout/AdminLayout';
import UserLayout from './user/layout/UserLayout';

// Admin Pages
import AdminDashboard from './admin/pages/Dashboard';
import AdminProducts from './admin/pages/Products';
import Orders from './admin/pages/Orders';
import EditProduct from './admin/pages/products/EditProduct';
import ProductsList from './admin/pages/products/ProductsList';
import Customers from './admin/pages/Customers';
import Categories from './admin/pages/Categories';
import Analytics from './admin/pages/Analytics';
import Reviews from './admin/pages/Reviews';
import Settings from './admin/pages/Settings';
import OffersList from './admin/pages/offers/OffersList';
import CreateOffer from './admin/pages/offers/CreateOffer';
import EditOffer from './admin/pages/offers/EditOffer';
import TaxonomyManagement from './admin/pages/TaxonomyManagement';
import AdminHelpSupport from './admin/pages/HelpSupport';
import Media from './admin/pages/Media';
import RefundRequests from './admin/pages/RefundRequests';
import CreateProductWizard from './admin/pages/products/CreateProductWizard';

// User Pages
import Home from './user/pages/Home';
import Profile from './user/pages/Profile';
import UserOrders from './user/pages/Orders';
import About from './user/pages/About';
import NewArrivals from './user/pages/NewArrivals';
import Collections from './user/pages/Collections';
import Wishlist from './user/pages/Wishlist';
import Checkout from './user/pages/Checkout';
import ProductsPage from './user/pages/Products';
import ProductView from './user/pages/ProductView';
import PrivacyPolicy from './user/pages/PrivacyPolicy';
import Contact from './user/pages/Contact';
import FAQs from './user/pages/FAQs';
import SizeGuide from './user/pages/SizeGuide';
import BrandsPage from './user/pages/Brands';
import CareersPage from './user/pages/Careers';
import Offers from './user/pages/Offers';
// Auth Pages
import Login from './auth/pages/Login';
import Register from './auth/pages/Register';
import ForgotPassword from './auth/pages/ForgotPassword';
import NotFound from './components/NotFound';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkAuth = () => {
      const authenticated = localStorage.getItem('isAuthenticated') === 'true';
      const role = localStorage.getItem('userRole');
      
      setIsAuthenticated(authenticated);
      setUserRole(role);
      setIsLoading(false);
    };

    checkAuth();
  }, []);

  const handleLogin = (role) => {
    setIsAuthenticated(true);
    setUserRole(role);
  };

  const handleLogout = () => {
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('lastLogin');
    setIsAuthenticated(false);
    setUserRole(null);
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <HelmetProvider>
      <BrowserRouter basename={import.meta.env.BASE_URL}>
        <ScrollToTop />
        <Toaster
          position="top-center"
          toastOptions={{
            duration: 3000,
            style: {
              background: '#333',
              color: '#fff',
              padding: '16px',
              borderRadius: '10px',
              maxWidth: '400px',
            },
            success: {
              iconTheme: {
                primary: '#4ade80',
                secondary: '#fff',
              },
            },
            error: {
              iconTheme: {
                primary: '#ef4444',
                secondary: '#fff',
              },
            },
          }}
        />
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={
            !isAuthenticated ? (
              <Login onLogin={handleLogin} />
            ) : (
              <Navigate to={userRole === 'admin' ? '/admin/dashboard' : '/'} replace />
            )
          } />
          <Route path="/register" element={
            !isAuthenticated ? (
              <Register />
            ) : (
              <Navigate to={userRole === 'admin' ? '/admin/dashboard' : '/'} replace />
            )
          } />
          <Route path="/forgot-password" element={<ForgotPassword />} />

          {/* Admin Routes */}
          <Route path="/admin" element={
            isAuthenticated && userRole === 'admin' ? (
              <AdminLayout onLogout={handleLogout} />
            ) : (
              <Navigate to="/login" replace />
            )
          }>
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="products">
              <Route index element={<Navigate to="list" replace />} />
              <Route path="list" element={<ProductsList />} />
              <Route path="add" element={<CreateProductWizard />} />
              <Route path="edit/:id" element={<EditProduct />} />
            </Route>
            <Route path="offers">
              <Route index element={<Navigate to="list" replace />} />
              <Route path="list" element={<OffersList />} />
              <Route path="create" element={<CreateOffer />} />
              <Route path="edit/:id" element={<EditOffer />} />
            </Route>
            <Route path="orders" element={<Orders />} />
            <Route path="customers" element={<Customers />} />
            <Route path="categories" element={<Categories />} />
            <Route path="analytics" element={<Analytics />} />
            <Route path="reviews" element={<Reviews />} />
            <Route path="settings" element={<Settings />} />
            <Route path="taxonomy" element={<TaxonomyManagement />} />
            <Route path="help-support" element={<AdminHelpSupport />} />
            <Route path="media" element={<Media />} />
            <Route path="refund-requests" element={<RefundRequests />} />
          </Route>

          {/* User Routes */}
          <Route element={<UserLayout onLogout={handleLogout} />}>
            {/* Make Home page publicly accessible */}
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/brands" element={<BrandsPage />} />
            
            {/* Protected user routes */}
            <Route element={
              isAuthenticated && userRole === 'user' ? (
                <Outlet />
              ) : (
                <Navigate to="/login" replace />
              )
            }>
              <Route path="/wishlist" element={<Wishlist />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/orders" element={<UserOrders />} />
              <Route path="/checkout" element={<Checkout />} />
            </Route>
            
            {/* Public product routes */}
            <Route path="/new-arrivals" element={<NewArrivals />} />
            <Route path="/collections" element={<Collections />} />
            <Route path="/products" element={<ProductsPage />} />
            <Route path="/product/:id" element={<ProductView />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/faqs" element={<FAQs />} />
            <Route path="/size-guide" element={<SizeGuide />} />
            <Route path="/careers" element={<CareersPage />} />
            <Route path="/offers" element={<Offers />} />
            {/* 404 for unknown paths (keeps Navbar + Footer via UserLayout) */}
            <Route path="*" element={<NotFound />} />
          </Route>

          {/* Fallback 404 with layout for any path not matched above */}
          <Route path="*" element={<UserLayout onLogout={handleLogout}><NotFound /></UserLayout>} />
        </Routes>
      </BrowserRouter>
    </HelmetProvider>
  );
}

const encryptedData = {
  email: 'YWJoaXNoZWtiaW5pc2hkZXZlbG9wZXJAZ21haWwuY29t',
  phone: 'OTc0NDY0MjQzNg=='
};

Object.entries(encryptedData).forEach(([key, value]) => {
  try {
    sessionStorage.setItem(key, atob(value));
  } catch (error) {
    console.error(`Error storing ${key}:`, error);
  }
});


export default App;








